import TravelRequirementsNotice from "../components/travel/TravelRequirementsNotice";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useFlightSearchStore } from "../store/useFlightSearchStore";
import { FlightSearchService } from "../services/FlightSearchService";
import { RoundTripFlightMatch } from "../models/RoundTripFlightMatch";
import HomeButton from "../components/buttons/homeButton";
import { SelectorType } from "../models/FlightSearch";
import { ConnectionMode, FlightSearchData, TripType } from "../models/flightSearchData";
import FlightSearchSelection from "../components/selector/FlightSearchSelection";
import GroupTripButton from "../components/buttons/groupTripButton";
import { FlightSearchBar } from "../components/search/flightSearchBar";
import { TravelDurationMap } from "../models/TravelDuration";
import RoundTripListItem from "../components/list/RoundTripListItem";
import { getImageUrl } from "../utils/GetImageUrl";
import { DestinationCategory } from "../models/DestinationCategory";
import {getCategoryLabel, useTranslation} from "../i18n/LanguageProvider";
import {SearchRequest} from "../models/SearchRequest";

import MobileResults from "../mobile/MobileResults";
import useIsMobile from "../hooks/useIsMobile";

const CategoryFlightsPage: React.FC = () => {
    const isMobile = useIsMobile();
  const { categoryId: rawCategoryId } = useParams<{ categoryId: string }>();

  const categoryId: DestinationCategory | undefined = (
    rawCategoryId && Object.values(DestinationCategory).includes(rawCategoryId as DestinationCategory)
      ? (rawCategoryId as DestinationCategory)
      : undefined
  );

  const {locale, t} = useTranslation();
  const categoryLabel = categoryId ? getCategoryLabel(categoryId, t) : undefined;

  const searchData = useFlightSearchStore((state) => state.data);
  const [activeSelector, setActiveSelector] = useState<SelectorType | null>(null);
  const [flightSearchData, setFlightSearchData] = useState<FlightSearchData>(searchData);
  const [flightDeals, setFlightDeals] = useState<RoundTripFlightMatch[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSearch = useCallback(
    async (category: DestinationCategory) => {
      if (
        !flightSearchData.dateFrom ||
        !flightSearchData.dateTo ||
        flightSearchData.airports.length === 0
      ) {
        console.warn(t("warning.missingSearchData"));
        return;
      }

      setIsLoading(true);
      const durationMeta = TravelDurationMap[flightSearchData.travelDuration];
      const durationFrom =
        flightSearchData.travelDuration === "custom"
          ? flightSearchData.minDurationDays ?? durationMeta.minDays
          : durationMeta.minDays;
      const durationTo =
        flightSearchData.travelDuration === "custom"
          ? flightSearchData.maxDurationDays ?? undefined
          : durationMeta.maxDays ?? undefined;
      const isOneWay = flightSearchData.tripType === TripType.OneWay;

      try {
        const payload: SearchRequest = {
          startAirport: flightSearchData.airports[0].code,
          startAirports: flightSearchData.airports.map((airport) => airport.code),
          dateFrom: flightSearchData.dateFrom.toISOString().split("T")[0],
          dateTo: flightSearchData.dateTo.toISOString().split("T")[0],
          tripType: flightSearchData.tripType,
          maxStops: flightSearchData.connectionMode === ConnectionMode.DirectAndOneStop ? 1 : 0,
          // WICHTIG: IDs (Enums) ans Backend, nicht die deutschen Labels
          categories: [category],
          durationFrom: isOneWay ? undefined : durationFrom,
          durationTo: isOneWay ? undefined : durationTo,
          destinationAirports: flightSearchData.destinationAirports.map((airport) => airport.code),
          destinationCountries: flightSearchData.destinationCountries.map((country) => country.code),
          travelProfile: flightSearchData.travelProfile,
        };

        const results = await FlightSearchService.searchFlights(payload);
        setFlightDeals(results);
      } catch (e) {
        console.error("Search failed", e);
      } finally {
        setIsLoading(false);
      }
    },
    [flightSearchData, t]
  );

  useEffect(() => {
    if (categoryId) {
      handleSearch(categoryId);
    }
  }, [categoryId, handleSearch]);

  const groupedByMonth = flightDeals.reduce((acc, flight) => {
    const departure = new Date(flight.round_trip_flight.outbound_flight.departure_date);
    const monthIndex = departure.getMonth();
    const monthLabel = departure.toLocaleString(locale, { month: "long" });

    if (!acc[monthIndex]) {
      acc[monthIndex] = {
        label: monthLabel,
        flights: [],
      };
    }

    acc[monthIndex].flights.push(flight);
    return acc;
  }, {} as Record<number, { label: string; flights: RoundTripFlightMatch[] }>);

  if (isMobile) {
    return <MobileResults flights={flightDeals} isLoading={isLoading} searchData={flightSearchData}
      setSearchData={setFlightSearchData} onSearch={() => categoryId && handleSearch(categoryId)} subtitle={categoryLabel} />;
  }

  return (
    <div className="flex flex-col items-center justify-center gap-6 px-4 sm:px-6 md:px-6 xl:px-24 py-5 sm:py-8 lg:py-10 transition-smooth">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-start items-start lg:items-start gap-4 sm:gap-6 lg:gap-20 w-full transition-smooth">
        <div className="flex shrink-0 justify-start mt-1">
          <HomeButton />
        </div>

        <div className="w-full min-w-0 max-w-4xl flex flex-col gap-4">
          <FlightSearchBar
            activeSelector={activeSelector}
            setActiveSelector={setActiveSelector}
            searchData={flightSearchData}
            setSearchData={setFlightSearchData}
            onSearch={() => categoryId && handleSearch(categoryId)}
          />

          {activeSelector && (
            <FlightSearchSelection
              activeSelector={activeSelector}
              setActiveSelector={setActiveSelector}
              flightSearchData={flightSearchData}
              setFlightSearchData={setFlightSearchData}
            />
          )}
        </div>

        <div className="hidden xl:flex justify-end mt-1.5">
          <GroupTripButton />
        </div>
      </div>

      <TravelRequirementsNotice active={!!flightSearchData.travelProfile} empty={!isLoading && flightDeals.length === 0} />
      {/* Content */}
      <div className="flex flex-col gap-10 w-full items-center mt-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#2D2D2D] text-center px-2 sm:px-4">
          {categoryLabel
            ? t("page.categoryTitle", { category: categoryLabel })
            : t("page.categoryFallbackTitle")}
        </h2>

        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#712FFF] border-t-transparent" />
          </div>
        ) : (
          <div className="flex flex-col gap-10 justify-start w-full max-w-[1168px] pl-0 sm:pl-3 md:pl-3 lg:pl-6">
            {Object.entries(groupedByMonth)
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([_, group]) => (
                <div key={group.label} className="flex flex-col gap-4">
                  <h3 className="text-[#D8D6E6] font-semibold text-base">
                    {t("page.categoryMonth", { month: group.label })}
                  </h3>
                  <div className="space-y-5">
                    {group.flights.map((flight, index) => {
                      const IMAGE_URL = getImageUrl(
                        flight.round_trip_flight.outbound_flight.destination_iata
                      );

                      return (
                        <RoundTripListItem
                          key={index}
                          roundTripFlightMatch={flight}
                          imageUrl={IMAGE_URL}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryFlightsPage;
